// ==============================|| FIELD DEFINITIONS ||============================== //

// Which fields a screen offers is authored in code (Weightrax.Domain.VisitFieldDefaults and its
// vehicle and material counterparts). What is configurable is how each one behaves, and that is
// configured in two places — against a company, and against a site that chooses to override it —
// so the shape and its parsing live here rather than in either api module.

// Mirrors Weightrax.Domain.Enums.FieldUsageEnum. Serialized as its numeric value: the API
// configures no string enum converter.
//
// Required predates the visit screen telling entry from exit, and kept its value so saved lists
// keep their behaviour: on the visit list it reads as Required Out (demanded when the visit
// completes). RequiredIn is visit-only — demanded on every save from the first — and anywhere else
// the server reads it as plain Required.
export const FIELD_USAGE = { Required: 1, Optional: 2, Hidden: 3, RequiredIn: 4 } as const;
export type FieldUsage = (typeof FIELD_USAGE)[keyof typeof FIELD_USAGE];

// Mirrors Weightrax.Domain.Enums.FieldTypeEnum.
export const FIELD_TYPE = {
  InternallyMapped: 0,
  ListTypeLookup: 1,
  Numeric: 2,
  FreeText: 3,
  Calculated: 4,
  CustomCalculation: 5
} as const;
export type FieldType = (typeof FIELD_TYPE)[keyof typeof FIELD_TYPE];

// Mirrors Weightrax.Domain.FieldOption. One entry configures one field on one screen; which screen
// it belongs to is decided by the list it sits in, not by the option itself.
export interface FieldOption {
  /** Code identifier, `Table.Property` — not editable, the screens map on it. */
  key: string;
  label: string;
  usage: FieldUsage;
  fieldType: FieldType;
  /** ListType name backing a ListTypeLookup field; null for every other field type. */
  lookupListType: string | null;
  /** The formula behind a CustomCalculation field, e.g. `[Visit.NetWeightKg] / 1000`; null otherwise. */
  formula: string | null;
  /** Places a CustomCalculation result is rounded to; null for every other field type. */
  decimalPlaces: number | null;
}

const USAGE_VALUES: readonly number[] = Object.values(FIELD_USAGE);
const FIELD_TYPE_VALUES: readonly number[] = Object.values(FIELD_TYPE);

/**
 * The lists are authored in code and reconciled server side, so this only has to survive a
 * malformed payload — anything it cannot read is dropped rather than guessed at, since a half-read
 * field definition would misconfigure the screen it belongs to.
 */
export function parseFieldDefinitions(raw: unknown): FieldOption[] {
  if (!Array.isArray(raw)) return [];

  return raw.flatMap((item) => {
    const option = (item ?? {}) as Record<string, unknown>;
    if (typeof option.key !== 'string' || !option.key) return [];

    const usage = USAGE_VALUES.includes(option.usage as number) ? (option.usage as FieldUsage) : FIELD_USAGE.Hidden;
    const fieldType = FIELD_TYPE_VALUES.includes(option.fieldType as number) ? (option.fieldType as FieldType) : FIELD_TYPE.FreeText;

    return [
      {
        key: option.key,
        label: typeof option.label === 'string' ? option.label : option.key,
        usage,
        fieldType,
        lookupListType: fieldType === FIELD_TYPE.ListTypeLookup && typeof option.lookupListType === 'string' ? option.lookupListType : null,
        formula: fieldType === FIELD_TYPE.CustomCalculation && typeof option.formula === 'string' ? option.formula : null,
        decimalPlaces: fieldType === FIELD_TYPE.CustomCalculation && typeof option.decimalPlaces === 'number' ? option.decimalPlaces : null
      }
    ];
  });
}

/**
 * A site's copy of a list, where the site is allowed not to have one.
 *
 * Null is the whole point: it is how the site records "use the company's list", and it has to
 * survive the round trip untouched — parsed to an empty array it would read as an override that
 * hides every field.
 */
export function parseOptionalFieldDefinitions(raw: unknown): FieldOption[] | null {
  return raw === null || raw === undefined ? null : parseFieldDefinitions(raw);
}
